import React, { useCallback, useState } from 'react'
import { Box, Stack, TextField } from '@mui/material'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { apiGet } from '../../../../generic/Api_Functions'
import MosSmartTable from '../../common/MosSmartTable'
import useExternalJobColumns from '../hooks/useExternalJobColumns'
import { RETURNED_RESOURCE } from '../hooks/useReturnedExternalJobTrips'
import { defaultFromDate, toDateInputValue, fromDateInputValue } from '../utils/returnedPeriod'

// Story 100 (S100 parity, 2026-09-02 sun commit 9790585a9e/PR sun#3709): Sun extended
// `GET .../returned`'s row with the SAME field names as `ExternalJobTripRowDto`
// (pendingDispatch/dispatched) -- `fabricationOrder`, `job`, `technology`,
// `promisedReturnDate`/`promisedReturnDurationLabel`, `deviationMinutes` -- so the Returned tab now
// reuses `useExternalJobColumns` directly (`includeActions: false`, RN33/S105) instead of a
// dedicated column set. The ONE field pair Sun did not rename is the actual dates
// (`actualStart`/`actualEnd`, predating this convention, lote 15 vs. lote 17 of sun): normalizing
// them here, at the fetch boundary, is the single place that removes that divergence without
// teaching the shared columns a per-population field name.
const normalizeReturnedRow = (row) => {
  const { actualStart, actualEnd, ...rest } = row
  return { ...rest, actualDispatch: actualStart, actualReturn: actualEnd }
}

/**
 * Story 100 (S104, RN47 + DN-7): the "Returned" tab -- the widget's history. Defaults to the last
 * thirty days and pages server-side via `MosSmartTable` (SmartMateriaUITable): every page and every
 * period change re-asks Sun (`GET mos/externalJob/returned?from&page&size`), nothing is
 * re-paginated in memory. The period filter sits right above the table, in the SAME place
 * SmartMateriaUITable-based widgets put their own controls (MachineMaterialWidget's consumption
 * mode selector, PlannedJobListWidget's period select) -- not floating loose next to a title.
 */
const ReturnedJobsHistory = () => {
  const { t_ } = useSmartTranslation()
  const columns = useExternalJobColumns({ includeActions: false })
  const [from, setFrom] = useState(defaultFromDate)
  const [reloadFlag, setReloadFlag] = useState(0)

  // SAT #3089/#3971: the table's whole request (its column filters and order too, not only page/size)
  // plus the period -- same reason as the other two tabs (ExternalJobsWidget.jsx).
  const dataFetch = useCallback(async (pagination) => {
    const page = await apiGet(RETURNED_RESOURCE, { ...pagination, from: from.toISOString() })
    return { ...page, content: (page?.content ?? []).map(normalizeReturnedRow) }
  }, [from])

  const handleFromChange = (event) => {
    setFrom(fromDateInputValue(event.target.value))
    setReloadFlag((value) => value + 1)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Stack direction='row' sx={{ p: 1 }}>
        <TextField
          label={t_('From')}
          type='date'
          size='small'
          value={toDateInputValue(from)}
          onChange={handleFromChange}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <MosSmartTable
          columns={columns}
          dataFetch={dataFetch}
          forceLoad={reloadFlag}
          tablePageSize={10}
          pageable
          showSearchHeader
        />
      </Box>
    </Box>
  )
}

export default ReturnedJobsHistory
